import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileUploader } from "@/components/profile/FileUploader";
import { 
  Plus, 
  FileText,
  Loader2,
  X,
  Save,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ImageIcon
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RosterField } from "@/types/packet-management";

interface TemplateBuilderTabProps {
  selectedFields: string[];
  setSelectedFields: (fields: string[]) => void;
  templateName: string;
  setTemplateName: (name: string) => void;
  templatePrice: number;
  setTemplatePrice: (price: number) => void;
  templateLayout: 'one-page-per-team' | 'condensed' | 'detailed';
  setTemplateLayout: (layout: 'one-page-per-team' | 'condensed' | 'detailed') => void;
  headerText: string;
  setHeaderText: (text: string) => void;
  footerText: string;
  setFooterText: (text: string) => void;
  includePageNumbers: boolean;
  setIncludePageNumbers: (include: boolean) => void;
  logoUrl: string;
  setLogoUrl: (url: string) => void;
  isUploadingLogo: boolean;
  setIsUploadingLogo: (uploading: boolean) => void;
  rosterFields: RosterField[];
  fieldCategories: string[];
  handleLogoUpload: (file: File | string) => void;
  saveTemplate: () => void;
  clearTemplate: () => void;
  addField: (fieldName: string) => void;
  removeField: (fieldName: string) => void;
  moveFieldUp: (index: number) => void;
  moveFieldDown: (index: number) => void;
  handleDragEnd: (event: any) => void;
  sensors: any;
}

// Sortable field item component
const SortableFieldItem: React.FC<{ 
  field: { name: string; label: string; type: string; }, 
  index: number,
  onRemove: (fieldName: string) => void,
  onMoveUp: (index: number) => void,
  onMoveDown: (index: number) => void,
  totalCount: number
}> = ({ field, index, onRemove, onMoveUp, onMoveDown, totalCount }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="flex items-center justify-between p-3 bg-muted/50 rounded border group"
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
            {index + 1}
          </span>
          <button
            className="cursor-grab hover:cursor-grabbing p-1 hover:bg-muted rounded"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div>
          <span className="font-medium">{field.label}</span>
          <Badge variant="outline" className="text-xs ml-2">{field.type}</Badge>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
          onClick={() => onMoveUp(index)}
          disabled={index === 0}
          title="Move Up"
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
          onClick={() => onMoveDown(index)}
          disabled={index === totalCount - 1}
          title="Move Down"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
          onClick={() => onRemove(field.name)}
          title="Remove Field"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export const TemplateBuilderTab: React.FC<TemplateBuilderTabProps> = ({
  selectedFields,
  setSelectedFields,
  templateName,
  setTemplateName,
  templatePrice,
  setTemplatePrice,
  templateLayout,
  setTemplateLayout,
  headerText,
  setHeaderText,
  footerText,
  setFooterText,
  includePageNumbers,
  setIncludePageNumbers,
  logoUrl,
  setLogoUrl,
  isUploadingLogo,
  setIsUploadingLogo,
  rosterFields,
  fieldCategories,
  handleLogoUpload,
  saveTemplate,
  clearTemplate,
  addField,
  removeField,
  moveFieldUp,
  moveFieldDown,
  handleDragEnd,
  sensors
}) => {
  return (
    <div className="space-y-8">
      {/* Main Template Builder */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Available Fields */}
        <Card>
          <CardHeader>
            <CardTitle>Available Fields</CardTitle>
            <CardDescription>
              Click fields to add them to your template
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-3">
                {fieldCategories.map(category => {
                  const categoryFields = rosterFields.filter(field => field.category === category);
                  if (categoryFields.length === 0) return null;
                  
                  return (
                    <div key={category} className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">{category}</h4>
                      <div className="space-y-1">
                        {categoryFields.map(field => (
                          <Button
                            key={field.name}
                            variant={selectedFields.includes(field.name) ? "default" : "outline"}
                            size="sm"
                            className="w-full justify-start"
                            onClick={() => addField(field.name)}
                            disabled={selectedFields.includes(field.name)}
                          >
                            <Plus className="h-3 w-3 mr-2" />
                            {field.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Template Preview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Template Builder
            </CardTitle>
            <CardDescription>
              Configure your template layout and fields
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Template Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name-builder">Template Name</Label>
                  <Input
                    id="template-name-builder"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-price">Template Price ($)</Label>
                  <Input
                    id="template-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={(templatePrice / 100).toFixed(2)}
                    onChange={(e) => setTemplatePrice(Math.round(parseFloat(e.target.value || "0") * 100))}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-layout">Layout Style</Label>
                  <Select value={templateLayout} onValueChange={(value: 'one-page-per-team' | 'condensed' | 'detailed') => setTemplateLayout(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select layout" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-page-per-team">One Page Per Team</SelectItem>
                      <SelectItem value="condensed">Condensed</SelectItem>
                      <SelectItem value="detailed">Detailed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Selected Fields */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Selected Fields</h3>
                  <Badge variant="outline">{selectedFields.length} fields</Badge>
                </div>
                
                {selectedFields.length === 0 ? (
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">No fields selected</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Click fields to add them to your template
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Select fields from the left panel to build your roster layout
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={selectedFields} strategy={verticalListSortingStrategy}>
                        {selectedFields.map((fieldName, index) => {
                          const field = rosterFields.find(f => f.name === fieldName);
                          if (!field) return null;
                          
                          return (
                            <SortableFieldItem
                              key={fieldName}
                              field={field}
                              index={index}
                              onRemove={removeField}
                              onMoveUp={moveFieldUp}
                              onMoveDown={moveFieldDown}
                              totalCount={selectedFields.length}
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </div>

              {/* Template Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={clearTemplate}
                  variant="outline"
                  className="flex-1"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
                <Button
                  onClick={saveTemplate}
                  disabled={!templateName || selectedFields.length === 0}
                  className="flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Layout Settings - Completely Separate Section */}
      <div className="border-t pt-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Layout Settings</h2>
          <p className="text-muted-foreground">Configure header, footer, and logo settings for your templates</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Header Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Header Settings
              </CardTitle>
              <CardDescription>
                Configure the header that appears at the top of each packet page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="header-text">Header Text</Label>
                <Textarea
                  id="header-text"
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  placeholder="Enter header text"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include-page-numbers"
                  checked={includePageNumbers}
                  onCheckedChange={(checked) => setIncludePageNumbers(checked as boolean)}
                />
                <Label htmlFor="include-page-numbers">Include page numbers</Label>
              </div>
            </CardContent>
          </Card>

          {/* Footer Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Footer Settings
              </CardTitle>
              <CardDescription>
                Configure the footer that appears at the bottom of each packet page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="footer-text">Footer Text</Label>
                <Textarea
                  id="footer-text"
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value)}
                  placeholder="Enter footer text"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Logo Upload
              </CardTitle>
              <CardDescription>
                Upload a logo to include in your packet templates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {logoUrl ? (
                <div className="space-y-3">
                  <div className="border rounded-lg p-4 text-center">
                    <img
                      src={logoUrl}
                      alt="Template Logo"
                      className="max-h-24 mx-auto object-contain"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLogoUrl("")}
                    className="w-full"
                  >
                    Remove Logo
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleLogoUpload(file);
                      }}
                      className="w-full text-sm text-muted-foreground
                               file:mr-4 file:py-2 file:px-4
                               file:rounded-md file:border-0
                               file:text-sm file:font-medium
                               file:bg-primary file:text-primary-foreground
                               hover:file:bg-primary/90"
                      disabled={isUploadingLogo}
                    />
                  </div>
                  {isUploadingLogo && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Uploading logo...</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};